import React, { useState, useEffect, useRef } from 'react';
import { 
  FaSearch, FaUserMinus, FaTimes, FaExclamationTriangle, FaFileAlt, 
  FaDownload, FaFileExcel, FaSpinner, FaCheck, FaPause, FaPlay, FaStop
} from 'react-icons/fa';
import { Student } from '../types';
import { API_BASE_URL } from '../utils/apiConfig';
import * as XLSX from 'xlsx';
import { 
  getValidLevelsCount, 
  getValidLevelsAchieved,
  calculateProgressByAgeTramos
} from '../utils/gradeCalculationForWithdrawal';
import { lenguajesArtisticosDimension } from './LenguajeArtistico';
import { habilidadesComunicacionalesDimension } from './HabilidadesComunicacionales';
import { studentIdsMatch, extractRutFromStudent } from '../utils/studentIdUtils';

// Función para obtener nombre de dimensión por ID
const getDimensionNameById = (dimensionId: number): string => {
  const DIMENSION_NAMES: { [key: number]: string } = {
    1: "Autoconciencia y Voluntad",
    2: "Habilidades de pensamiento",
    3: "Habilidades comunicacionales en el lenguaje español",
    4: "Corporalidad y movimiento",
    5: "Lenguaje Artístico",
    6: "Motivación para el aprendizaje",
    7: "Encuentro con la Naturaleza y Sociedad",
    8: "Habilidades sociales",
    9: "Pensamiento matemático",
    10: "Acercamiento a la lengua inglesa",
    11: "Amor a Dios y sentido Cristiano"
  };
  
  return DIMENSION_NAMES[dimensionId] || `Dimensión ${dimensionId}`;
};

interface ActiveStudent {
  id: string;
  name: string;
  course: string;
  birthDate: string;
  gender?: string;
  skillLevels?: Array<{
    dimensionId: number;
    skillId: number;
    level: number;
  }>;
  qualitativeDescription?: string;
}

interface WithdrawnStudent {
  id: string;
  originalId?: string;
  name: string;
  course: string;
  birthDate?: string;
  withdrawalDate: string;
  skillLevels?: Array<{
    dimensionId: number;
    skillId: number;
    level: number | null;
  }>;
  finalProgressSummary: {
    totalSkills: number;
    averageLevel: number;
    completedSkills: number;
    completionPercentage: number;
  };
  finalGrades?: {
    overall: number;
    byDimension: { [key: string]: { grade: number; skillCount: number } };
    bySubject?: { [subject: string]: { grade: number; skillCount: number; details: any[] } };
    dimensionSummary?: { 
      [key: string]: { 
        dimensionName: string;
        areaName: string;
        expectedPoints: number;
        achievedPoints: number;
        percentage: number;
        grade: number;
        skillCount: number;
        totalSkills: number;
        achievedValidLevels?: number;
        totalValidLevels?: number;
        completeness?: number;
        targetAge?: number;
      } 
    };
  };
  reason?: string;
  additionalNotes?: string;
  processedBy?: string;
  detailedReport?: {
    strengths: string[];
    areasForImprovement: string[];
    recommendations: string[];
  };
}

interface InformeNotasRetirosProps {
  students: Student[];
  onSectionChange: (section: string) => void;
}

interface ExportState {
  isRunning: boolean;
  isPaused: boolean;
  currentIndex: number;
  totalCount: number;
  processedCount: number;
  errorCount: number;
  errors: string[];
  startTime: number;
  estimatedTimeRemaining: number;
}

const InformeNotasRetiros: React.FC<InformeNotasRetirosProps> = ({
  students,
  onSectionChange
}) => {
  const [activeStudents, setActiveStudents] = useState<ActiveStudent[]>([]);
  const [withdrawnStudents, setWithdrawnStudents] = useState<WithdrawnStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<ActiveStudent | null>(null);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'withdrawn' | 'massiveExport'>('active');
  const [selectedWithdrawnStudent, setSelectedWithdrawnStudent] = useState<WithdrawnStudent | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<WithdrawnStudent | null>(null);
  
  // Estados para exportación masiva
  const [selectedCoursesForExport, setSelectedCoursesForExport] = useState<string[]>([]);
  const [isExportingMassive, setIsExportingMassive] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportState, setExportState] = useState<ExportState>({
    isRunning: false,
    isPaused: false,
    currentIndex: 0,
    totalCount: 0,
    processedCount: 0,
    errorCount: 0,
    errors: [],
    startTime: 0,
    estimatedTimeRemaining: 0
  });
  const exportStateRef = useRef(exportState);
  
  useEffect(() => {
    exportStateRef.current = exportState;
  }, [exportState]);

  const courses = Array.from(new Set(activeStudents.map(student => student.course))).sort();

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Load active students
  const loadActiveStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/active?search=${encodeURIComponent(searchTerm)}&course=${encodeURIComponent(selectedCourse)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        setActiveStudents([]);
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.students)) {
        setActiveStudents(data.students);
      } else {
        setActiveStudents([]);
      }
    } catch (error) {
      setActiveStudents([]);
    } finally {
      if (initialLoading) {
        setTimeout(() => setLoading(false), 500);
      } else {
        setLoading(false);
      }
    }
  };

  // Load withdrawn students
  const loadWithdrawnStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/withdrawn`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        setWithdrawnStudents([]);
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.students)) {
        setWithdrawnStudents(data.students);
      } else {
        setWithdrawnStudents([]);
      }
    } catch (error) {
      setWithdrawnStudents([]);
    } finally {
      if (initialLoading) {
        setTimeout(() => setLoading(false), 500);
      } else {
        setLoading(false);
      }
    }
  };

  // Withdraw student
  const withdrawStudent = async () => {
    if (!selectedStudent) return;

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/withdraw`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: selectedStudent.id,
          reason: withdrawalReason,
          additionalNotes: additionalNotes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        alert(`✅ Estudiante ${selectedStudent.name} retirado exitosamente`);
        setShowWithdrawModal(false);
        setSelectedStudent(null);
        setWithdrawalReason('');
        setAdditionalNotes('');
        
        await loadActiveStudents();
        await loadWithdrawnStudents();
      } else {
        alert(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      alert(`❌ Error al retirar estudiante`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate student progress
  const calculateProgress = (student: ActiveStudent) => {
    if (!student || !student.skillLevels || student.skillLevels.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    
    try {
      const total = student.skillLevels.length;
      const totalScore = student.skillLevels.reduce((sum, skill) => {
        if (skill && skill.level !== undefined && skill.level !== null) {
          return sum + (skill.level + 1) * 20 + 20;
        }
        return sum;
      }, 0);
      
      const averageScore = total > 0 ? totalScore / total : 0;
      const percentage = Math.round(averageScore);
      
      const completed = student.skillLevels.filter(skill => 
        skill && skill.level !== undefined && skill.level !== null && skill.level > 0
      ).length;
      
      return { completed, total, percentage };
    } catch (error) {
      return { completed: 0, total: 0, percentage: 0 };
    }
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      await loadActiveStudents();
      await loadWithdrawnStudents();

      setTimeout(() => {
        setInitialLoading(false);
      }, 3000);
    };

    loadData();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (activeTab === 'active') {
        loadActiveStudents();
      } else if (activeTab === 'withdrawn') {
        loadWithdrawnStudents();
      }
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(delayedSearch);
  }, [activeTab, searchTerm, selectedCourse]);

  const openWithdrawModal = (student: ActiveStudent) => {
    setSelectedStudent(student);
    setShowWithdrawModal(true);
    setWithdrawalReason('');
    setAdditionalNotes('');
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setSelectedStudent(null);
    setWithdrawalReason('');
    setAdditionalNotes('');
  };

  const openReportModal = (student: WithdrawnStudent) => {
    setSelectedWithdrawnStudent(student);
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setSelectedWithdrawnStudent(null);
  };

  // Update existing withdrawn students with subject grades
  const updateExistingWithdrawnStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/update-withdrawn-grades`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`✅ ${result.message}`);
        await loadWithdrawnStudents();
      } else {
        alert(`❌ Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error actualizando estudiantes retirados:', error);
      alert(`❌ Error al actualizar estudiantes retirados: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Export withdrawal report as PDF
  const exportWithdrawalReport = async () => {
    if (!selectedWithdrawnStudent) return;

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/withdrawal-report/${selectedWithdrawnStudent.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('text/html')) {
        const htmlContent = await response.text();
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `Informe_Retiro_${selectedWithdrawnStudent.name}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `Informe_Retiro_${selectedWithdrawnStudent.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      alert('✅ Informe descargado exitosamente');
    } catch (error) {
      console.error('Error descargando informe:', error);
      alert(`❌ Error al descargar el informe: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (student: WithdrawnStudent) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  // Delete student permanently
  const deleteStudentPermanently = async () => {
    if (!studentToDelete) {
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('No se encontró el token de autenticación');
        return;
      }

      const studentId = studentToDelete.originalId || studentToDelete.id;

      const response = await fetch(`${API_BASE_URL}/api/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setWithdrawnStudents(prev => prev.filter(student =>
          (student.originalId || student.id) !== studentId
        ));

        alert(`Estudiante "${studentToDelete.name}" eliminado permanentemente del sistema.`);
        closeDeleteModal();
      } else {
        throw new Error(data.message || 'Error al eliminar el estudiante');
      }
    } catch (error) {
      alert(`Error al eliminar el estudiante: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter students for display
  const filteredActiveStudents = activeStudents.filter(student => {
    const matchesSearch = !searchTerm || 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toString().includes(searchTerm);
    const matchesCourse = !selectedCourse || student.course === selectedCourse;
    return matchesSearch && matchesCourse;
  });

  // ============== FUNCIONALIDAD DE EXPORTACIÓN MASIVA DE INFORMES ==============

  const handleSelectAllCoursesForExport = () => {
    if (selectedCoursesForExport.length === courses.length) {
      setSelectedCoursesForExport([]);
    } else {
      setSelectedCoursesForExport([...courses]);
    }
  };

  const handleCourseToggleForExport = (course: string) => {
    if (selectedCoursesForExport.includes(course)) {
      setSelectedCoursesForExport(prev => prev.filter(c => c !== course));
    } else {
      setSelectedCoursesForExport(prev => [...prev, course]);
    }
  };

  const findCompleteStudent = (activeStudent: ActiveStudent): Student | undefined => {
    const matched = students.find(s =>
      studentIdsMatch(s.id, activeStudent.id) ||
      ((s as any).originalId && studentIdsMatch((s as any).originalId, activeStudent.id))
    );
    if (matched) return matched;

    if (activeStudent.skillLevels) {
      return {
        id: activeStudent.id,
        name: activeStudent.name,
        course: activeStudent.course,
        birthDate: activeStudent.birthDate,
        skillLevels: activeStudent.skillLevels,
        qualitativeDescription: activeStudent.qualitativeDescription || ''
      };
    }

    console.warn(
      `[Export] Estudiante omitido: ${activeStudent.name} (id=${activeStudent.id}, curso=${activeStudent.course}) — sin coincidencia en props.students ni skillLevels`
    );
    return undefined;
  };

  // Mapeo DIRECTO de dimensiones a asignaturas del curriculum nacional
  // NOTA: Dimensiones 1, 6, 8 (Orientación) se EXCLUYEN porque afectan el promedio
  // NOTA: Dimensiones 3 y 5 se manejan especialmente por subdimensiones
  const DIMENSION_TO_SUBJECT: { [key: number]: string | string[] } = {
    // 1: EXCLUIDO - Autoconciencia y Voluntad (era Orientación)
    2: 'Lenguaje y Comunicación',               // Habilidades de pensamiento
    // 3: Se maneja especialmente abajo (Lenguaje español + Escritura → Lenguaje y Comunicación)
    4: 'Educación Física y Salud',              // Corporalidad y movimiento
    // 5: Se maneja especialmente abajo (Lenguaje Musical → Música, Lenguaje Visual → Arte Visual)
    // 6: EXCLUIDO - Motivación para el aprendizaje (era Orientación)
    7: ['Ciencias Naturales', 'Historia, Geografía y Ciencias Sociales'], // Encuentro con la Naturaleza y Sociedad
    // 8: EXCLUIDO - Habilidades sociales (era Orientación)
    9: 'Matemáticas',                           // Pensamiento matemático
    10: 'Inglés',                               // Acercamiento a la lengua inglesa
    11: 'Religión'                              // Amor a Dios y sentido Cristiano
  };

  // IDs de habilidades para las subdimensiones de Lenguaje Artístico (Dimensión 5)
  // Lenguaje Musical: IDs 1-36
  // Lenguaje Visual: IDs 37+
  const LENGUAJE_MUSICAL_SKILL_IDS = lenguajesArtisticosDimension.subDimensions?.[0]?.skills.map(s => s.id) || [];
  const LENGUAJE_VISUAL_SKILL_IDS = lenguajesArtisticosDimension.subDimensions?.[1]?.skills.map(s => s.id) || [];

  // IDs de habilidades para las subdimensiones de Habilidades Comunicacionales (Dimensión 3)
  // Subdimensión 1: Habilidades comunicacionales en español
  // Subdimensión 2: Escritura
  const LENGUAJE_ESPANOL_SKILL_IDS = habilidadesComunicacionalesDimension.subDimensions?.[0]?.skills.map(s => s.id) || [];
  const ESCRITURA_SKILL_IDS = habilidadesComunicacionalesDimension.subDimensions?.[1]?.skills.map(s => s.id) || [];

  // Asignaturas del curriculum nacional (SIN Orientación)
  const SUBJECTS = [
    'Lenguaje y Comunicación',
    'Matemáticas',
    'Ciencias Naturales',
    'Historia, Geografía y Ciencias Sociales',
    'Inglés',
    'Educación Física y Salud',
    'Arte Visual y Tecnología',
    'Música',
    'Religión'
  ];

  // Función para convertir porcentaje a nota chilena (1.0 - 7.0)
  // Escala al 60%: 60% de logro = nota 4.0
  const percentageToGrade = (percentage: number): number => {
    if (percentage >= 100) return 7.0;
    if (percentage <= 0) return 1.0;
    
    // Escala lineal: 0% = 1.0, 60% = 4.0, 100% = 7.0
    if (percentage < 60) {
      // 0-60% -> 1.0-4.0
      return 1.0 + (percentage / 60) * 3.0;
    } else {
      // 60-100% -> 4.0-7.0
      return 4.0 + ((percentage - 60) / 40) * 3.0;
    }
  };

  // Calcular notas usando el sistema correcto de niveles válidos por dimensión
  // Usa getValidLevelsCount y getValidLevelsAchieved de gradeCalculationForWithdrawal.ts
  // que calculan correctamente los niveles válidos (no "No aplica") hasta la edad objetivo
  // ESPECIAL: 
  // - Dimensiones 1, 6, 8 (Orientación) se EXCLUYEN
  // - Dimensión 3 (Habilidades Comunicacionales) se separa en subdimensiones para Lenguaje y Comunicación
  // - Dimensión 5 (Lenguaje Artístico) se separa en subdimensiones para Música y Arte Visual
  const calculateGradesFromSkillLevels = (student: Student): { [subject: string]: number } => {
    const grades: { [subject: string]: { totalExpected: number; totalAchieved: number; dimensionCount: number } } = {};
    
    // Inicializar todas las asignaturas
    SUBJECTS.forEach(subject => {
      grades[subject] = { totalExpected: 0, totalAchieved: 0, dimensionCount: 0 };
    });

    // Dimensiones normales (sin 1, 3, 5, 6, 8 que se excluyen o manejan especialmente)
    // 1, 6, 8 = Orientación (EXCLUIDAS)
    // 3 = Habilidades Comunicacionales (se maneja por subdimensiones)
    // 5 = Lenguaje Artístico (se maneja por subdimensiones)
    const DIMENSION_IDS = [2, 4, 7, 9, 10, 11];
    
    // Procesar dimensiones normales
    DIMENSION_IDS.forEach(dimensionId => {
      const dimensionName = getDimensionNameById(dimensionId);
      
      // Obtener la(s) asignatura(s) para esta dimensión
      const subjectMapping = DIMENSION_TO_SUBJECT[dimensionId];
      if (!subjectMapping) {
        return;
      }

      // Manejar tanto una sola asignatura como múltiples asignaturas
      const subjects = Array.isArray(subjectMapping) ? subjectMapping : [subjectMapping];

      // Calcular niveles válidos totales (denominador) - niveles hasta la edad objetivo
      const totalValidLevels = getValidLevelsCount(dimensionId, dimensionName, student.course, student.skillLevels);
      
      // Calcular niveles logrados válidos (numerador) - niveles que el estudiante ha alcanzado
      const achievedValidLevels = getValidLevelsAchieved(dimensionId, dimensionName, student.course, student.skillLevels || []);

      // Solo procesar si hay niveles válidos para esta dimensión
      if (totalValidLevels > 0) {
        // Aplicar a todas las asignaturas mapeadas
        subjects.forEach(subject => {
          if (!grades[subject]) {
            return;
          }
          
          // Acumular niveles esperados y logrados
          grades[subject].totalExpected += totalValidLevels;
          grades[subject].totalAchieved += achievedValidLevels;
          grades[subject].dimensionCount++;
        });
      }
    });

    // ============================================================
    // CASO ESPECIAL: Dimensión 3 - Habilidades Comunicacionales (subdimensiones)
    // Lenguaje español + Escritura → Lenguaje y Comunicación
    // ============================================================
    const dimensionId3 = 3;
    const dimensionName3 = "Habilidades comunicacionales en el lenguaje español";

    // Procesar Lenguaje español → Lenguaje y Comunicación
    if (LENGUAJE_ESPANOL_SKILL_IDS.length > 0) {
      const lenguajeResult = calculateProgressByAgeTramos(
        dimensionId3,
        dimensionName3,
        student.course,
        student.skillLevels || [],
        LENGUAJE_ESPANOL_SKILL_IDS
      );
      
      if (lenguajeResult.expectedTotal > 0) {
        grades['Lenguaje y Comunicación'].totalExpected += lenguajeResult.expectedTotal;
        grades['Lenguaje y Comunicación'].totalAchieved += lenguajeResult.achievedTotal;
        grades['Lenguaje y Comunicación'].dimensionCount++;
        console.log(`📝 Lenguaje (Habilidades comunicacionales): ${lenguajeResult.achievedTotal}/${lenguajeResult.expectedTotal} = ${lenguajeResult.percentage.toFixed(1)}%`);
      }
    }

    // Procesar Escritura → Lenguaje y Comunicación (si hay habilidades válidas para la edad)
    if (ESCRITURA_SKILL_IDS.length > 0) {
      const escrituraResult = calculateProgressByAgeTramos(
        dimensionId3,
        dimensionName3,
        student.course,
        student.skillLevels || [],
        ESCRITURA_SKILL_IDS
      );
      
      if (escrituraResult.expectedTotal > 0) {
        grades['Lenguaje y Comunicación'].totalExpected += escrituraResult.expectedTotal;
        grades['Lenguaje y Comunicación'].totalAchieved += escrituraResult.achievedTotal;
        grades['Lenguaje y Comunicación'].dimensionCount++;
        console.log(`✍️ Escritura: ${escrituraResult.achievedTotal}/${escrituraResult.expectedTotal} = ${escrituraResult.percentage.toFixed(1)}%`);
      }
    }

    // ============================================================
    // CASO ESPECIAL: Dimensión 5 - Lenguaje Artístico (subdimensiones)
    // Lenguaje Musical → Música
    // Lenguaje Visual → Arte Visual y Tecnología
    // ============================================================
    const dimensionId5 = 5;
    const dimensionName5 = "Lenguaje Artístico";

    // Procesar Lenguaje Musical → Música
    if (LENGUAJE_MUSICAL_SKILL_IDS.length > 0) {
      const musicalResult = calculateProgressByAgeTramos(
        dimensionId5,
        dimensionName5,
        student.course,
        student.skillLevels || [],
        LENGUAJE_MUSICAL_SKILL_IDS
      );
      
      if (musicalResult.expectedTotal > 0) {
        grades['Música'].totalExpected += musicalResult.expectedTotal;
        grades['Música'].totalAchieved += musicalResult.achievedTotal;
        grades['Música'].dimensionCount++;
        console.log(`🎵 Música (Lenguaje Musical): ${musicalResult.achievedTotal}/${musicalResult.expectedTotal} = ${musicalResult.percentage.toFixed(1)}%`);
      }
    }

    // Procesar Lenguaje Visual → Arte Visual y Tecnología
    if (LENGUAJE_VISUAL_SKILL_IDS.length > 0) {
      const visualResult = calculateProgressByAgeTramos(
        dimensionId5,
        dimensionName5,
        student.course,
        student.skillLevels || [],
        LENGUAJE_VISUAL_SKILL_IDS
      );
      
      if (visualResult.expectedTotal > 0) {
        grades['Arte Visual y Tecnología'].totalExpected += visualResult.expectedTotal;
        grades['Arte Visual y Tecnología'].totalAchieved += visualResult.achievedTotal;
        grades['Arte Visual y Tecnología'].dimensionCount++;
        console.log(`🎨 Arte Visual (Lenguaje Visual): ${visualResult.achievedTotal}/${visualResult.expectedTotal} = ${visualResult.percentage.toFixed(1)}%`);
      }
    }

    // Calcular notas finales
    const finalGrades: { [subject: string]: number } = {};
    
    SUBJECTS.forEach(subject => {
      const data = grades[subject];
      if (data.dimensionCount > 0 && data.totalExpected > 0) {
        // Porcentaje de logro = (niveles logrados / niveles esperados) * 100
        const achievementPercentage = (data.totalAchieved / data.totalExpected) * 100;
        finalGrades[subject] = Math.round(percentageToGrade(achievementPercentage) * 10) / 10;
        
        console.log(`📊 ${subject}: ${data.totalAchieved}/${data.totalExpected} = ${achievementPercentage.toFixed(1)}% -> Nota ${finalGrades[subject]}`);
      } else {
        finalGrades[subject] = 1.0; // Sin datos = nota mínima
      }
    });

    return finalGrades;
  };

  // Calcular promedio general
  const calculateOverallGrade = (subjectGrades: { [subject: string]: number }): number => {
    const grades = Object.values(subjectGrades);
    if (grades.length === 0) return 1.0;
    
    const sum = grades.reduce((acc, grade) => acc + grade, 0);
    return Math.round((sum / grades.length) * 10) / 10;
  };

  // Exportación masiva de notas a Excel
  const exportGradesToExcel = async () => {
    if (selectedCoursesForExport.length === 0) {
      alert('Por favor selecciona al menos un curso para exportar.');
      return;
    }

    // Buscar estudiantes completos (con skillLevels) de los props
    const studentsToExport: Student[] = [];
    activeStudents.forEach(activeStudent => {
      if (selectedCoursesForExport.includes(activeStudent.course)) {
        const completeStudent = findCompleteStudent(activeStudent);
        if (completeStudent) {
          studentsToExport.push(completeStudent);
        }
      }
    });

    if (studentsToExport.length === 0) {
      alert('No se encontraron estudiantes con datos completos para exportar.');
      return;
    }

    setIsExportingMassive(true);
    setExportProgress(0);
    
    const initialExportState: ExportState = {
      isRunning: true,
      isPaused: false,
      currentIndex: 0,
      totalCount: studentsToExport.length,
      processedCount: 0,
      errorCount: 0,
      errors: [],
      startTime: Date.now(),
      estimatedTimeRemaining: 0
    };
    
    setExportState(initialExportState);
    exportStateRef.current = initialExportState;

    console.log(`🚀 Iniciando exportación de notas Excel para ${studentsToExport.length} estudiantes en ${selectedCoursesForExport.length} cursos`);

    try {
      // Crear datos para el Excel
      const excelData: any[] = [];
      let processedStudents = 0;
      let errorCount = 0;

      // Procesar estudiantes
      for (let i = 0; i < studentsToExport.length; i++) {
        if (!exportStateRef.current.isRunning) {
          console.log('🛑 Exportación detenida');
          break;
        }

        const student = studentsToExport[i];
        
        try {
          console.log(`📄 Procesando ${i + 1}/${studentsToExport.length}: ${student.name} (${student.course})`);
          console.log(`📊 SkillLevels del estudiante: ${student.skillLevels?.length || 0} habilidades`);
          
          // Calcular notas por asignatura usando la función local
          const subjectGrades = calculateGradesFromSkillLevels(student);
          const overallGrade = calculateOverallGrade(subjectGrades);
          
          console.log(`📊 Notas calculadas para ${student.name}:`, subjectGrades);
          console.log(`📊 Promedio general: ${overallGrade}`);
          
          // Crear fila para el Excel
          const row: any = {
            'RUT/ID': extractRutFromStudent(student),
            'Nombre': student.name,
            'Curso': student.course,
            'Promedio General': overallGrade
          };

          // Agregar nota de cada asignatura
          SUBJECTS.forEach(subject => {
            row[subject] = subjectGrades[subject] || 1.0;
          });

          excelData.push(row);
          processedStudents++;
          
          // Actualizar progreso
          const updatedState = {
            ...exportStateRef.current,
            currentIndex: i + 1,
            processedCount: processedStudents
          };
          exportStateRef.current = updatedState;
          setExportState(updatedState);
          
        } catch (error) {
          const errorMsg = `Error procesando ${student.name}: ${error instanceof Error ? error.message : 'Error desconocido'}`;
          console.error(`❌ ${errorMsg}`);
          errorCount++;
          
          const errorState = {
            ...exportStateRef.current,
            errorCount: exportStateRef.current.errorCount + 1,
            errors: [...exportStateRef.current.errors, errorMsg]
          };
          exportStateRef.current = errorState;
          setExportState(errorState);
        }
        
        const progressPercentage = ((i + 1) / studentsToExport.length) * 90;
        setExportProgress(progressPercentage);
      }

      // Generar archivo Excel
      setExportProgress(95);
      console.log('📊 Generando archivo Excel...');

      // Crear workbook y worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 15 },  // RUT/ID
        { wch: 35 },  // Nombre
        { wch: 20 },  // Curso
        { wch: 15 },  // Promedio General
        ...SUBJECTS.map(() => ({ wch: 12 }))  // Asignaturas
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Informe de Notas');

      // Descargar archivo
      const coursesStr = selectedCoursesForExport.length > 3 
        ? `${selectedCoursesForExport.length}_cursos` 
        : selectedCoursesForExport.join('_').replace(/\s+/g, '-');
      const fileName = `Informe_Notas_${coursesStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);

      setExportProgress(100);
      
      alert(`✅ Exportación completada!\n\n📊 Estudiantes procesados: ${processedStudents}/${studentsToExport.length}\n❌ Errores: ${errorCount}\n\n📁 Archivo descargado: ${fileName}`);

    } catch (error) {
      console.error('Error en exportación de notas:', error);
      alert(`❌ Error en la exportación: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsExportingMassive(false);
      const finalState = {
        ...exportStateRef.current,
        isRunning: false
      };
      exportStateRef.current = finalState;
      setExportState(finalState);
    }
  };

  const pauseExport = () => {
    const newState = { ...exportStateRef.current, isPaused: true };
    exportStateRef.current = newState;
    setExportState(newState);
  };

  const resumeExport = () => {
    const newState = { ...exportStateRef.current, isPaused: false };
    exportStateRef.current = newState;
    setExportState(newState);
  };

  const stopExport = () => {
    const newState = { ...exportStateRef.current, isRunning: false, isPaused: false };
    exportStateRef.current = newState;
    setExportState(newState);
    setIsExportingMassive(false);
  };

  const formatTime = (ms: number): string => {
    if (ms < 1000) return '< 1s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  // ============== RENDERIZADO ==============

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <FaFileAlt className="text-2xl text-indigo-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Informes de Notas y Retiros</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'active'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('active')}
        >
          Estudiantes Activos ({activeStudents.length})
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'withdrawn'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('withdrawn')}
        >
          Estudiantes Retirados ({withdrawnStudents.length})
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'massiveExport'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('massiveExport')}
        >
          <FaFileExcel className="inline mr-2" />
          Exportar Notas a Excel
        </button>
      </div>

      {/* Search and Filters - For active students tab */}
      {activeTab === 'active' && (
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder={`Buscar entre ${activeStudents.length} estudiantes por nombre o ID...`}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">Todos los cursos ({activeStudents.length} estudiantes)</option>
                {courses.map(course => {
                  const courseCount = activeStudents.filter(s => s.course === course).length;
                  return (
                    <option key={course} value={course}>
                      {course} ({courseCount} estudiantes)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <p className="flex items-center">
              <FaSearch className="mr-2" />
              💡 <strong>Tip:</strong> En esta sección puedes retirar estudiantes. Para editar o agregar estudiantes, ve a la sección "Estudiantes".
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {(loading || initialLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{initialLoading ? 'Cargando estudiantes...' : 'Cargando...'}</p>
        </div>
      )}

      {/* Active Students List */}
      {activeTab === 'active' && !loading && !initialLoading && (
        <div className="space-y-4">
          {filteredActiveStudents.length === 0 ? (
            <div className="text-center py-8">
              <FaSearch className="mx-auto h-12 w-12 text-gray-400" />
              <p className="text-gray-500 mt-4">
                {searchTerm || selectedCourse ? 
                  'No se encontraron estudiantes con los filtros aplicados' : 
                  'No hay estudiantes activos disponibles'
                }
              </p>
              {(searchTerm || selectedCourse) && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedCourse(''); }}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                📊 Mostrando <strong>{filteredActiveStudents.length} estudiantes</strong>
                {searchTerm && (
                  <span> que coinciden con "{searchTerm}"</span>
                )}
                {selectedCourse && (
                  <span> del curso {selectedCourse}</span>
                )}
              </div>
              
              {filteredActiveStudents.map((student) => {
                const progress = calculateProgress(student);
                return (
                  <div 
                    key={student.id} 
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start">
                      <div className="flex-grow">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 mr-4">{student.name}</h3>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            ID: {student.id}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Curso:</span> {student.course}
                          </div>
                          <div>
                            <span className="font-medium">Fecha Nacimiento:</span> {student.birthDate}
                          </div>
                          <div>
                            <span className="font-medium">Progreso:</span> {progress.completed}/{progress.total} ({progress.percentage}%)
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <button
                          onClick={() => openWithdrawModal(student)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                        >
                          <FaUserMinus className="mr-2" />
                          Retirar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Withdrawn Students List */}
      {activeTab === 'withdrawn' && !loading && !initialLoading && (
        <div className="space-y-4">
          {withdrawnStudents.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Actualizar Estudiantes Retirados</h3>
                  <p className="text-sm text-blue-700">
                    Agregar notas por asignaturas a estudiantes retirados anteriormente ({withdrawnStudents.length} estudiantes)
                  </p>
                </div>
                <button
                  onClick={updateExistingWithdrawnStudents}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <FaDownload className="mr-2" />
                  {loading ? 'Actualizando...' : 'Actualizar Todos'}
                </button>
              </div>
            </div>
          )}

          {withdrawnStudents.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay estudiantes retirados</p>
          ) : (
            withdrawnStudents.map((student) => (
              <div key={student.id || student.originalId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-700 mr-4">{student.name || 'Nombre no disponible'}</h3>
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded">
                        ID: {student.id || student.originalId || 'N/A'}
                      </span>
                      <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded ml-2">
                        RETIRADO
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Curso:</span> {student.course || 'No disponible'}
                      </div>
                      <div>
                        <span className="font-medium">Fecha Retiro:</span> {(() => {
                          try {
                            return student.withdrawalDate ? 
                              new Date(student.withdrawalDate).toLocaleDateString() : 
                              'No disponible';
                          } catch (error) {
                            return 'Fecha inválida';
                          }
                        })()}
                      </div>
                      <div>
                        <span className="font-medium">Progreso Final:</span> {student.finalProgressSummary?.completedSkills || 0}/{student.finalProgressSummary?.totalSkills || 0} ({student.finalProgressSummary?.completionPercentage || 0}%)
                      </div>
                      <div>
                        <span className="font-medium">Nota Final Promedio:</span> {student.finalGrades?.overall?.toFixed(1) || 'N/A'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openReportModal(student)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                      title="Ver informe completo del estudiante retirado"
                    >
                      <FaFileAlt className="mr-2" />
                      Ver Informe
                    </button>
                    <button
                      onClick={() => openDeleteModal(student)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                      title="Eliminar definitivamente del sistema"
                    >
                      <FaTimes className="mr-2" />
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Massive Export Tab */}
      {activeTab === 'massiveExport' && !loading && !initialLoading && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <div className="bg-green-100 p-3 rounded-full">
                <FaFileExcel className="text-2xl text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-900">Exportar Informe de Notas a Excel</h3>
                <p className="text-green-700 mt-1">
                  Selecciona los cursos para generar un informe Excel con las notas de todos los estudiantes.
                  Las notas se calculan a partir de los niveles de logro, mapeados a las asignaturas del curriculum nacional.
                </p>
                <div className="mt-3 text-sm text-green-600">
                  <strong>Asignaturas incluidas:</strong> Lenguaje, Matemáticas, Ciencias, Historia, Inglés, Ed. Física, Arte, Música, Religión
                </div>
              </div>
            </div>
          </div>

          {/* Controles de selección */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-800">
              Cursos Disponibles ({courses.length})
            </h4>
            <button
              onClick={handleSelectAllCoursesForExport}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {selectedCoursesForExport.length === courses.length ? (
                <>
                  <FaTimes size={16} />
                  <span>Deseleccionar Todos</span>
                </>
              ) : (
                <>
                  <FaCheck size={16} />
                  <span>Seleccionar Todos</span>
                </>
              )}
            </button>
          </div>

          {/* Grid de cursos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {courses.map(course => {
              const studentsInCourse = activeStudents.filter(s => s.course === course).length;
              const isSelected = selectedCoursesForExport.includes(course);
              
              return (
                <div
                  key={course}
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isSelected 
                      ? 'border-green-500 bg-green-50 shadow-sm' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => handleCourseToggleForExport(course)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <FaCheck className="text-white" size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{course}</p>
                      <p className="text-sm text-gray-500">{studentsInCourse} estudiantes</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resumen de selección */}
          {selectedCoursesForExport.length > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                {selectedCoursesForExport.length} cursos seleccionados • {
                  activeStudents.filter(s => selectedCoursesForExport.includes(s.course)).length
                } estudiantes
              </p>
              <p className="text-sm text-green-600 mt-1">
                Cursos: {selectedCoursesForExport.join(', ')}
              </p>
            </div>
          )}

          {/* Botón de exportación */}
          <div className="flex justify-center">
            <button
              onClick={exportGradesToExcel}
              disabled={isExportingMassive || selectedCoursesForExport.length === 0}
              className={`px-8 py-4 rounded-xl font-medium transition-all duration-200 shadow-lg flex items-center space-x-3 ${
                isExportingMassive || selectedCoursesForExport.length === 0
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transform hover:scale-105'
              }`}
            >
              {isExportingMassive ? (
                <>
                  <FaSpinner className="animate-spin" size={20} />
                  <span>Generando Excel...</span>
                </>
              ) : (
                <>
                  <FaFileExcel size={20} />
                  <span>Exportar Notas a Excel</span>
                </>
              )}
            </button>
          </div>

          {/* Panel de progreso */}
          {isExportingMassive && (
            <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    exportState.isPaused ? 'bg-yellow-400' : 'bg-green-400 animate-pulse'
                  }`}></div>
                  <span className="font-medium text-gray-700">
                    {exportState.isPaused ? 'Pausado' : 'Procesando...'}
                  </span>
                </div>
                <div className="flex space-x-2">
                  {exportState.isPaused ? (
                    <button
                      onClick={resumeExport}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center"
                    >
                      <FaPlay className="mr-1" size={12} />
                      Reanudar
                    </button>
                  ) : (
                    <button
                      onClick={pauseExport}
                      className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 flex items-center"
                    >
                      <FaPause className="mr-1" size={12} />
                      Pausar
                    </button>
                  )}
                  <button
                    onClick={stopExport}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center"
                  >
                    <FaStop className="mr-1" size={12} />
                    Detener
                  </button>
                </div>
              </div>

              {/* Barra de progreso */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress}%` }}
                ></div>
              </div>

              {/* Estadísticas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-600 font-medium">Procesados</p>
                  <p className="text-xl font-bold text-blue-800">
                    {exportState.processedCount}/{exportState.totalCount}
                  </p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-green-600 font-medium">Progreso</p>
                  <p className="text-xl font-bold text-green-800">
                    {exportProgress.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-red-600 font-medium">Errores</p>
                  <p className="text-xl font-bold text-red-800">
                    {exportState.errorCount}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-purple-600 font-medium">Tiempo Est.</p>
                  <p className="text-xl font-bold text-purple-800">
                    {formatTime(exportState.estimatedTimeRemaining)}
                  </p>
                </div>
              </div>

              {/* Lista de errores */}
              {exportState.errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg max-h-32 overflow-y-auto">
                  <p className="text-red-700 font-medium mb-2">Errores ({exportState.errors.length}):</p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {exportState.errors.slice(-5).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Withdrawal Confirmation Modal */}
      {showWithdrawModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <FaExclamationTriangle className="text-red-500 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Confirmar Retiro</h2>
              <button
                onClick={closeWithdrawModal}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                ¿Estás seguro de que deseas retirar al estudiante:
              </p>
              <div className="bg-gray-100 p-3 rounded">
                <p className="font-semibold">{selectedStudent.name}</p>
                <p className="text-sm text-gray-600">ID: {selectedStudent.id} | Curso: {selectedStudent.course}</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo del retiro *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={withdrawalReason}
                onChange={(e) => setWithdrawalReason(e.target.value)}
                required
              >
                <option value="">Seleccionar motivo</option>
                <option value="cambio_colegio">Cambio de colegio</option>
                <option value="cambio_ciudad">Cambio de ciudad</option>
                <option value="motivos_economicos">Motivos económicos</option>
                <option value="motivos_academicos">Motivos académicos</option>
                <option value="motivos_disciplinarios">Motivos disciplinarios</option>
                <option value="motivos_familiares">Motivos familiares</option>
                <option value="otros">Otros</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas adicionales
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Información adicional sobre el retiro..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <div className="flex">
                <FaExclamationTriangle className="text-yellow-400 mt-0.5 mr-2" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">⚠️ Acción irreversible</p>
                  <p>El estudiante será eliminado permanentemente de la lista activa y se creará un respaldo con su progreso actual.</p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={closeWithdrawModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={withdrawStudent}
                disabled={!withdrawalReason || loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Procesando...' : 'Confirmar Retiro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && selectedWithdrawnStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <FaFileAlt className="text-blue-500 text-2xl mr-3" />
                <h2 className="text-xl font-bold text-gray-900">Informe de Retiro</h2>
              </div>
              <button
                onClick={closeReportModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <FaTimes />
              </button>
            </div>
            
            {/* Student Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-blue-900 mb-2">Información del Estudiante</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium">Nombre:</span> {selectedWithdrawnStudent.name}</div>
                <div><span className="font-medium">ID:</span> {selectedWithdrawnStudent.id}</div>
                <div><span className="font-medium">Curso:</span> {selectedWithdrawnStudent.course}</div>
                <div><span className="font-medium">Fecha de Retiro:</span> {
                  selectedWithdrawnStudent.withdrawalDate ? 
                    new Date(selectedWithdrawnStudent.withdrawalDate).toLocaleDateString() : 
                    'No disponible'
                }</div>
              </div>
            </div>

            {/* Withdrawal Information */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-red-900 mb-2">Información del Retiro</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium">Motivo:</span> {selectedWithdrawnStudent.reason || 'No especificado'}</div>
                <div><span className="font-medium">Procesado por:</span> {selectedWithdrawnStudent.processedBy || 'No disponible'}</div>
                {selectedWithdrawnStudent.additionalNotes && (
                  <div className="md:col-span-2">
                    <span className="font-medium">Notas adicionales:</span> {selectedWithdrawnStudent.additionalNotes}
                  </div>
                )}
              </div>
            </div>

            {/* Final Progress */}
            {selectedWithdrawnStudent.finalProgressSummary && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-bold text-green-900 mb-2">Progreso Final</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{selectedWithdrawnStudent.finalProgressSummary.totalSkills}</div>
                    <div className="text-gray-600">Total Habilidades</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{selectedWithdrawnStudent.finalProgressSummary.completedSkills}</div>
                    <div className="text-gray-600">Completadas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{selectedWithdrawnStudent.finalProgressSummary.completionPercentage}%</div>
                    <div className="text-gray-600">Progreso Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{selectedWithdrawnStudent.finalGrades?.overall?.toFixed(1) || 'N/A'}</div>
                    <div className="text-gray-600">Nota Final Promedio</div>
                  </div>
                </div>
              </div>
            )}

            {/* Final Grades */}
            {selectedWithdrawnStudent.finalGrades && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-bold text-yellow-900 mb-2">Notas Finales</h3>

                {/* Nota General */}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-yellow-700">{selectedWithdrawnStudent.finalGrades.overall || 'N/A'}</div>
                  <div className="text-gray-600">Nota General (Escala 1.0 - 7.0)</div>
                </div>

                {/* Notas por Asignatura */}
                {selectedWithdrawnStudent.finalGrades?.bySubject && Object.keys(selectedWithdrawnStudent.finalGrades.bySubject).length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-yellow-900 mb-3">Notas por Asignatura:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(selectedWithdrawnStudent.finalGrades.bySubject).map(([subject, data]) => (
                        <div key={subject} className="bg-white p-3 rounded border">
                          <div className="flex justify-between items-center mb-1">
                            <div className="font-medium text-gray-900">{subject}</div>
                            <div className="text-yellow-700 font-bold">{data.grade}</div>
                          </div>
                          <div className="text-xs text-gray-600">
                            {data.skillCount === 0 ? 'Sin habilidades evaluadas' : `${data.skillCount} habilidades evaluadas`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notas por Dimensión */}
                {selectedWithdrawnStudent.finalGrades.byDimension && (
                  <div>
                    <h4 className="font-medium text-yellow-900 mb-2">Notas por Dimensión:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {Object.entries(selectedWithdrawnStudent.finalGrades.byDimension).map(([dim, data]) => {
                        const dimensionName = getDimensionNameById(parseInt(dim));
                        return (
                          <div key={dim} className="bg-white p-2 rounded border border-gray-200">
                            <div className="font-medium text-xs text-gray-900">{dimensionName}</div>
                            <div className="text-yellow-700 font-bold">{data.grade || 'N/A'}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
              <button
                onClick={exportWithdrawalReport}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <FaDownload className="mr-2" />
                {loading ? 'Generando...' : 'Descargar PDF'}
              </button>

              <button
                onClick={closeReportModal}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cerrar Informe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && studentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <FaTimes className="text-red-500 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Eliminar Estudiante Permanentemente</h2>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                ¿Estás seguro de que quieres eliminar permanentemente a <strong>{studentToDelete.name}</strong> del sistema?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800 text-sm">
                  ⚠️ <strong>Advertencia:</strong> Esta acción no se puede deshacer. Se eliminarán todos los datos del estudiante de la base de datos.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={deleteStudentPermanently}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <FaTimes className="mr-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformeNotasRetiros;

